import { useState, useEffect } from "react";
import { Tooltip } from "antd";
import EnterpriseModal from "../EnterpriseModal";
import EnterpriseDetailModal from "./EnterpriseDetailModal";
import { fetchEnterprisesBySceneId } from "@/services/dataserver";

interface SceneDetailItem {
  name: string;
  extractType: string;
  gid: string | number;
  objectid: string | number;
  geom: string;
}

interface EnterpriseItem {
  name: string;
  industry: string;
  legalPerson: string;
  address: string;
  insurance: string;
  _raw: Record<string, any>;
}

type TabKey = "parkProfile" | "enterpriseProfile" | "facilities";

interface InfoRowProps {
  icon: string;
  label: string;
  value: React.ReactNode;
  tooltip?: React.ReactNode;
}

function InfoRow({ icon, label, value, tooltip }: InfoRowProps) {
  const content = (
    <div className={`flex items-center gap-1.5 ${tooltip ? "cursor-help" : ""}`}>
      <i className={`${icon} text-slate-400 text-[11px]`} />
      <span className="text-[11px] text-slate-600">
        {label}：<span className="font-medium text-slate-700">{value}</span>
      </span>
    </div>
  );

  if (tooltip) {
    return <Tooltip title={tooltip}>{content}</Tooltip>;
  }
  return content;
}

export function IndustrialPanel({ scene }: { scene: SceneDetailItem }) {
  const [mainTab, setMainTab] = useState<TabKey>("parkProfile");
  const [activeModal, setActiveModal] = useState<'basic' | 'format' | 'directory' | null>(null);
  const [formatTab, setFormatTab] = useState<string>("status");
  const [enterpriseList, setEnterpriseList] = useState<EnterpriseItem[]>([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState<Record<string, any> | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  useEffect(() => {
    if (activeModal === "directory") {
      setPageNum(1);
    }
  }, [activeModal]);

  useEffect(() => {
    if (activeModal === "directory" && scene.gid) {
      setDirectoryLoading(true);
      fetchEnterprisesBySceneId(scene.gid, pageNum, pageSize)
        .then(({ data, total: t }) => {
          const list = Array.isArray(data) ? data : [];
          const mapped: EnterpriseItem[] = list.map((item: any) => ({
            name: item.name || item.NAME || item.qymc || '未知',
            industry: item.gbhyml || item.industry || item.INDUSTRY || item.hy || item.jjhy || '',
            legalPerson: item.legalPerson || item.LEGALPERSON || item.fr || item.fddbr || '',
            address: item.zcdz || item.address || item.ADDRESS || item.addr || item.zs || '',
            insurance: item.insurance || item.INSURANCE || item.sb || item.cbrs || '',
            _raw: item,
          }));
          setEnterpriseList(mapped);
          setTotal(t);
        })
        .catch((err) => {
          console.error('企业目录加载失败:', err);
          setEnterpriseList([]);
          setTotal(0);
        })
        .finally(() => setDirectoryLoading(false));
    }
  }, [activeModal, scene.gid, pageNum, pageSize]);

  const tabs = [
    { key: "parkProfile" as TabKey, label: "园区画像", icon: "ri-building-4-line" },
    { key: "enterpriseProfile" as TabKey, label: "企业画像", icon: "ri-group-line" },
    { key: "facilities" as TabKey, label: "周边配套", icon: "ri-store-2-line" },
  ];

  return (
    <>
      {/* Main Tabs */}
      <div className="flex border-b border-slate-200">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setMainTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors cursor-pointer ${
              mainTab === tab.key
                ? "text-orange-600 border-b-2 border-orange-500 bg-orange-50/50"
                : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
            }`}
          >
            <i className={`${tab.icon} text-[13px]`} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="max-h-[380px] overflow-y-auto">
        {mainTab === "parkProfile" && (
          <div className="px-4 py-3 space-y-3">
            {/* 基本信息 */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-information-line text-orange-400 text-[13px]" />
                <span className="text-xs font-semibold text-slate-600">基本信息</span>
              </div>
              <div className="bg-orange-50/50 rounded-lg px-3 py-3 space-y-2">
                <InfoRow icon="ri-building-4-line" label="园区名称" value={scene.name} />
                <InfoRow icon="ri-vip-crown-line" label="园区等级" value="江苏省文化产业示范园区" />
                <InfoRow icon="ri-stack-line" label="园区类型" value="产业园区" />
                <InfoRow icon="ri-global-line" label="园区官网" value="/" />
                <InfoRow icon="ri-map-pin-2-line" label="地址" value="南京市秦淮区运粮河西路57号" />
                <InfoRow icon="ri-government-line" label="行政区划" value="江苏省南京市秦淮区/320104" />
                <InfoRow icon="ri-focus-2-line" label="中心坐标" value="118.877432，32.014252" />
                <InfoRow icon="ri-landscape-line" label="园区面积" value="约54.10亩" />
              </div>
            </div>

            {/* 运营信息 */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-dashboard-line text-orange-400 text-[13px]" />
                <span className="text-xs font-semibold text-slate-600">运营信息</span>
              </div>
              <div className="bg-orange-50/50 rounded-lg px-3 py-3 space-y-2">
                <InfoRow icon="ri-building-2-line" label="物业公司" value="自有物业" />
                <InfoRow icon="ri-user-settings-line" label="管理单位" value="/" />
                <InfoRow icon="ri-money-cny-circle-line" label="租金" value="0.9-1.6元/m²/天" />
                <InfoRow icon="ri-bill-line" label="物业费" value="6元/m²/月" />
                <InfoRow icon="ri-flashlight-line" label="水/电价格" value="暂无" />
                <InfoRow icon="ri-parking-box-line" label="停车位" value="300个" />
                <InfoRow icon="ri-user-add-line" label="入住率" value="/" />
                <InfoRow icon="ri-user-unfollow-line" label="空置率" value="/" />
                <InfoRow icon="ri-group-line" label="企业数" value="266" />
                <InfoRow icon="ri-compass-3-line" label="招商方向" value="智能制造，人工智能，物联网科技，总部办公" />
              </div>
            </div>

            {/* 园区配套 */}
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-building-2-line text-orange-400 text-[13px]" />
                <span className="text-xs font-semibold text-slate-600">园区配套</span>
              </div>
              <div className="bg-orange-50/50 rounded-lg px-3 py-2.5">
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  设有众创空间、产业用房、梦想空间、展示中心、会议中心、休闲广场等设施，不仅提供办公空间，也包含企业孵化、成果展示与休闲交流的功能。
                </p>
              </div>
            </div>

            {/* 联系方式 */}
            <div className="bg-orange-50/50 rounded-lg px-3 py-2.5">
              <InfoRow icon="ri-phone-line" label="联系方式" value="025-66035573" />
            </div>
          </div>
        )}

        {mainTab === "enterpriseProfile" && (
          <div className="px-4 py-3 space-y-3">
            <div className="flex gap-1.5">
              {[
                { key: "basic" as const, label: "基础画像", icon: "ri-user-search-line" },
                { key: "format" as const, label: "业态分布", icon: "ri-pie-chart-line" },
                { key: "directory" as const, label: "企业目录", icon: "ri-book-3-line" },
              ].map((btn) => (
                <button
                  key={btn.key}
                  onClick={() => setActiveModal(btn.key)}
                  className="flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-[11px] font-medium transition-all cursor-pointer bg-orange-50 text-orange-600 hover:bg-orange-100 active:bg-orange-200 border border-orange-200"
                >
                  <i className={`${btn.icon} text-[12px]`} />
                  {btn.label}
                  <i className="ri-external-link-line text-[10px] ml-0.5" />
                </button>
              ))}
            </div>
            <div className="flex flex-col items-center justify-center py-6 text-[11px] text-slate-400 bg-slate-50/50 rounded-lg">
              <i className="ri-arrow-up-line text-slate-300 text-lg mb-1" />
              <span>点击上方按钮，在中央弹窗查看详情</span>
            </div>
          </div>
        )}

        {mainTab === "facilities" && (
          <div className="px-4 py-3 space-y-2">
            <div className="flex items-center gap-1.5 mb-2">
              <i className="ri-store-2-line text-orange-400 text-[13px]" />
              <span className="text-xs font-semibold text-slate-600">周边配套设施</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: "ri-hospital-line", label: "医院", count: "6", color: "#E24B4A" },
                { icon: "ri-school-line", label: "学校", count: "8", color: "#378ADD" },
                { icon: "ri-bus-line", label: "公交站", count: "15", color: "#639922" },
                { icon: "ri-subway-line", label: "地铁站", count: "3", color: "#534AB7" },
                { icon: "ri-restaurant-line", label: "餐饮", count: "42", color: "#EF9F27" },
                { icon: "ri-bank-line", label: "银行", count: "11", color: "#185FA5" },
                { icon: "ri-shopping-bag-line", label: "商超", count: "7", color: "#D85A30" },
                { icon: "ri-parking-line", label: "停车场", count: "9", color: "#0F6E56" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 bg-orange-50/50 rounded-lg px-2.5 py-2">
                  <i className={`${item.icon} text-slate-400 text-[14px]`} />
                  <div className="flex-1">
                    <div className="text-[11px] text-slate-500">{item.label}</div>
                    <div className="text-[11px] font-semibold text-slate-700">{item.count}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ==================== 企业弹窗 ==================== */}

      <EnterpriseModal
        visible={activeModal === "basic"}
        title="基础画像"
        onClose={() => setActiveModal(null)}
      >
        <div className="space-y-3 text-[12px] text-slate-500">
          <div className="flex items-center gap-1.5 mb-1">
            <i className="ri-bar-chart-grouped-line text-orange-500 text-sm" />
            <span className="text-sm font-semibold text-slate-600">企业概况</span>
          </div>
          <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
            <span className="text-slate-500">注册企业总数</span>
            <span className="font-medium text-slate-700">1666 家</span>
          </div>
          <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
            <span className="text-slate-500">在营企业</span>
            <span className="font-medium text-green-600">1097 家</span>
          </div>
          <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
            <span className="text-slate-500">已注销吊销</span>
            <span className="font-medium text-red-500">556 家</span>
          </div>
          <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
            <span className="text-slate-500">营业状态不明</span>
            <span className="font-medium text-amber-500">13 家</span>
          </div>

          <div className="flex items-center gap-1.5 mb-1 mt-4">
            <i className="ri-time-line text-orange-500 text-sm" />
            <span className="text-sm font-semibold text-slate-600">成立时长分布</span>
          </div>
          <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
            <span className="text-slate-500">一年内成立</span>
            <span className="font-medium text-slate-700">94 家</span>
          </div>
          <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
            <span className="text-slate-500">注册一到三年</span>
            <span className="font-medium text-slate-700">217 家</span>
          </div>
          <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
            <span className="text-slate-500">三到五年</span>
            <span className="font-medium text-slate-700">319 家</span>
          </div>
          <div className="flex items-center justify-between py-1.5">
            <span className="text-slate-500">五年以上</span>
            <span className="font-medium text-slate-700">468 家</span>
          </div>
        </div>
      </EnterpriseModal>

      <EnterpriseModal
        visible={activeModal === "format"}
        title="业态分布"
        onClose={() => setActiveModal(null)}
      >
        <div className="flex gap-1.5 mb-4 flex-wrap">
          {[
            { key: "status", label: "企业状态" },
            { key: "duration", label: "注册时长" },
            { key: "capital", label: "注册资金" },
            { key: "industry", label: "经营行业" },
            { key: "insurance", label: "参保人数" },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setFormatTab(tab.key)}
              className={`px-3 py-1.5 rounded text-[11px] font-medium transition-all cursor-pointer ${
                formatTab === tab.key
                  ? "bg-orange-500 text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {formatTab === "status" && (
          <div className="space-y-4">
            <div className="bg-orange-50/50 rounded-lg px-3 py-2.5 text-[12px] text-slate-600 leading-relaxed">
              曾经在<strong className="text-slate-700">{scene.name}</strong>内办公的企业共
              <strong className="text-slate-700">1666家</strong>，其中：
            </div>
            <div className="space-y-2.5">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200 px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-[12px] font-semibold text-green-700">存续 · 在业 · 开业</span>
                  </div>
                  <span className="text-[18px] font-bold text-green-600">65.8%</span>
                </div>
                <div className="text-[13px] font-semibold text-slate-700 ml-5">1097 家</div>
                <div className="mt-1.5 h-2 bg-green-100 rounded-full overflow-hidden ml-5">
                  <div className="h-full rounded-full bg-green-500" style={{width:'65.8%'}} />
                </div>
              </div>
              <div className="bg-gradient-to-r from-red-50 to-rose-50 rounded-lg border border-red-200 px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-[12px] font-semibold text-red-700">注销 · 吊销</span>
                  </div>
                  <span className="text-[18px] font-bold text-red-500">33.4%</span>
                </div>
                <div className="text-[13px] font-semibold text-slate-700 ml-5">556 家</div>
                <div className="mt-1.5 h-2 bg-red-100 rounded-full overflow-hidden ml-5">
                  <div className="h-full rounded-full bg-red-500" style={{width:'33.4%'}} />
                </div>
              </div>
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200 px-4 py-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-amber-500" />
                    <span className="text-[12px] font-semibold text-amber-700">状态未知</span>
                  </div>
                  <span className="text-[18px] font-bold text-amber-500">0.8%</span>
                </div>
                <div className="text-[13px] font-semibold text-slate-700 ml-5">13 家</div>
                <div className="mt-1.5 h-2 bg-amber-100 rounded-full overflow-hidden ml-5">
                  <div className="h-full rounded-full bg-amber-500" style={{width:'0.8%'}} />
                </div>
              </div>
            </div>
            <div className="text-center text-[11px] text-slate-400">
              入驻企业总计 <span className="font-semibold text-slate-600">1666</span> 家
            </div>
          </div>
        )}

        {formatTab === "duration" && (
          <div className="space-y-4">
            <div className="bg-orange-50/50 rounded-lg px-3 py-2.5 text-[12px] text-slate-600 leading-relaxed">
              入驻<strong className="text-slate-700">{scene.name}</strong>的
              <strong className="text-green-600">1098家</strong>存续企业中：
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-green-50 rounded-lg px-3 py-2.5 border border-green-100">
                <div className="text-[10px] text-green-600 font-medium mb-0.5">最新注册</div>
                <div className="text-[11px] font-medium text-slate-700 truncate">南京伍享融信息咨询有限公司</div>
                <div className="text-[10px] text-slate-400">2026年05月09日成立</div>
              </div>
              <div className="bg-amber-50 rounded-lg px-3 py-2.5 border border-amber-100">
                <div className="text-[10px] text-amber-600 font-medium mb-0.5">注册最久</div>
                <div className="text-[11px] font-medium text-slate-700 truncate">柯达（中国）股份有限公司南京办事处</div>
                <div className="text-[10px] text-slate-400">已成立 55年4个月11天</div>
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-slate-600 mb-2">注册时长分布</div>
              <div className="flex h-8 rounded-lg overflow-hidden mb-3">
                <div className="flex items-center justify-center text-[10px] text-white font-medium" style={{flex:'94',background:'#f97316',minWidth:0}}>&lt;1年</div>
                <div className="flex items-center justify-center text-[10px] text-white font-medium" style={{flex:'217',background:'#639922',minWidth:0}}>1-3年</div>
                <div className="flex items-center justify-center text-[10px] text-white font-medium" style={{flex:'319',background:'#EF9F27',minWidth:0}}>3-5年</div>
                <div className="flex items-center justify-center text-[10px] text-white font-medium" style={{flex:'360',background:'#E24B4A',minWidth:0}}>5-10年</div>
                <div className="flex items-center justify-center text-[10px] text-white font-medium" style={{flex:'108',background:'#534AB7',minWidth:0}}>10年+</div>
              </div>
              <div className="space-y-1.5">
                {[
                  { color: '#f97316', label: '1年内', count: '94家', pct: '8.6%', w: '8.6%' },
                  { color: '#639922', label: '1-3年', count: '217家', pct: '19.8%', w: '19.8%' },
                  { color: '#EF9F27', label: '3-5年', count: '319家', pct: '29.1%', w: '29.1%' },
                  { color: '#E24B4A', label: '5-10年', count: '360家', pct: '32.8%', w: '32.8%' },
                  { color: '#534AB7', label: '10年+', count: '108家', pct: '9.8%', w: '9.8%' },
                ].map((r) => (
                  <div key={r.label} className="flex items-center gap-2 py-1 border-b border-slate-100 last:border-b-0">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{background: r.color}} />
                    <span className="text-[11px] text-slate-600 w-16">{r.label}</span>
                    <span className="text-[11px] font-medium text-slate-700 w-10">{r.count}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{width: r.w, background: r.color}} />
                    </div>
                    <span className="text-[10px] text-slate-400 w-10 text-right">{r.pct}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {formatTab === "capital" && (
          <div className="space-y-4">
            <div className="bg-orange-50/50 rounded-lg px-3 py-2.5 text-[12px] text-slate-600 leading-relaxed">
              {scene.name}内现有注册资金数据的<strong className="text-slate-700">55家</strong>存续企业中：
            </div>
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg px-3 py-2.5 border border-orange-100">
              <div className="flex items-center gap-1.5 mb-1">
                <i className="ri-trophy-line text-amber-500 text-[13px]" />
                <span className="text-[10px] text-amber-600 font-medium">注册资金最多</span>
              </div>
              <div className="text-[12px] font-medium text-slate-700">江苏苏易通诉讼保全担保有限公司</div>
              <div className="text-[10px] text-slate-400 mt-0.5">法人：刘淑娟 ｜ 已成立 4年5个月19天</div>
              <div className="text-[10px] text-slate-400 leading-relaxed mt-1 bg-white/60 rounded px-2 py-1">
                主营：非融资担保服务；法律咨询；财务咨询；资产评估；房地产评估；破产清算服务；软件开发；信息咨询服务等
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-slate-600 mb-2">注册资金分布</div>
              <div className="flex h-8 rounded-lg overflow-hidden mb-3">
                <div className="flex items-center justify-center text-[9px] text-white font-medium px-0.5" style={{flex:'16',background:'#f97316'}}>10万内</div>
                <div className="flex items-center justify-center text-[9px] text-white font-medium px-0.5" style={{flex:'10',background:'#639922'}}>10-50万</div>
                <div className="flex items-center justify-center text-[9px] text-white font-medium px-0.5" style={{flex:'8',background:'#EF9F27'}}>50-100万</div>
                <div className="flex items-center justify-center text-[9px] text-white font-medium px-0.5" style={{flex:'10',background:'#E24B4A'}}>100-500万</div>
                <div className="flex items-center justify-center text-[9px] text-white font-medium px-0.5" style={{flex:'11',background:'#534AB7'}}>500万+</div>
              </div>
              <div className="space-y-1.5">
                {[
                  { color: '#f97316', label: '10万以内', count: '16家', pct: '29.1%', w: '29.1%' },
                  { color: '#639922', label: '10-50万', count: '10家', pct: '18.2%', w: '18.2%' },
                  { color: '#EF9F27', label: '50-100万', count: '8家', pct: '14.5%', w: '14.5%' },
                  { color: '#E24B4A', label: '100-500万', count: '10家', pct: '18.2%', w: '18.2%' },
                  { color: '#534AB7', label: '500万以上', count: '11家', pct: '20.0%', w: '20%' },
                ].map((r) => (
                  <div key={r.label} className="flex items-center gap-2 py-1 border-b border-slate-100 last:border-b-0">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{background: r.color}} />
                    <span className="text-[11px] text-slate-600 w-16">{r.label}</span>
                    <span className="text-[11px] font-medium text-slate-700 w-10">{r.count}</span>
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{width: r.w, background: r.color}} />
                    </div>
                    <span className="text-[10px] text-slate-400 w-10 text-right">{r.pct}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {formatTab === "industry" && (
          <div className="space-y-4">
            <div className="bg-orange-50/50 rounded-lg px-3 py-2.5 text-[12px] text-slate-600 leading-relaxed">
              {scene.name}<strong className="text-slate-700">1097家</strong>存续企业中，
              占比最多的行业是<strong className="text-orange-600">零售业</strong>（333家，31%），
              其次是<strong className="text-teal-600">商务服务业</strong>（185家，17.2%）。
            </div>
            <div className="flex h-8 rounded-lg overflow-hidden">
              <div className="flex items-center justify-center text-[10px] text-white font-medium" style={{flex:'333',background:'#f97316',minWidth:0}}>零售业</div>
              <div className="flex items-center justify-center text-[10px] text-white font-medium" style={{flex:'185',background:'#639922',minWidth:0}}>商务服务</div>
              <div className="flex items-center justify-center text-[10px] text-white font-medium" style={{flex:'141',background:'#EF9F27',minWidth:0}}>批发业</div>
              <div className="flex items-center justify-center text-[9px] text-white font-medium" style={{flex:'416',background:'#B4B2A9',minWidth:0}}>其它行业</div>
            </div>
            <div className="space-y-1.5">
              {[
                { color: '#f97316', label: '零售业', count: '333家', pct: '31.0%', w: '100%', pctColor: 'text-orange-600' },
                { color: '#639922', label: '商务服务业', count: '185家', pct: '17.2%', w: '55.5%', pctColor: 'text-green-600' },
                { color: '#EF9F27', label: '批发业', count: '141家', pct: '13.1%', w: '42.3%', pctColor: 'text-amber-600' },
                { color: '#B4B2A9', label: '其它行业', count: '416家', pct: '38.7%', w: '100%', pctColor: 'text-slate-400' },
              ].map((r) => (
                <div key={r.label} className="flex items-center gap-2 py-1.5 border-b border-slate-100 last:border-b-0">
                  <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{background: r.color}} />
                  <span className="text-[11px] text-slate-600 flex-1">{r.label}</span>
                  <span className="text-[11px] font-medium text-slate-700 w-12 text-right">{r.count}</span>
                  <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{width: r.w, background: r.color}} />
                  </div>
                  <span className={`text-[10px] font-medium w-10 text-right ${r.pctColor}`}>{r.pct}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {formatTab === "insurance" && (
          <div className="space-y-4">
            <div>
              <div className="text-[11px] font-semibold text-slate-600 mb-2">社保公开情况</div>
              <div className="bg-orange-50/50 rounded-lg px-3 py-2.5 text-[12px] text-slate-600 leading-relaxed mb-2">
                公开社保人数<strong className="text-orange-600">210家</strong>（19.1%），
                未公开<strong className="text-amber-600">887家</strong>（80.9%）。
              </div>
              <div className="flex h-6 rounded-lg overflow-hidden">
                <div className="flex items-center justify-center text-[10px] text-white font-medium" style={{flex:'210',background:'#f97316'}}>公开 210</div>
                <div className="flex items-center justify-center text-[10px] text-white font-medium" style={{flex:'887',background:'#B4B2A9'}}>未公开 887</div>
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-slate-600 mb-2">参保人数分布</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg border border-emerald-200 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-[11px] font-medium text-emerald-700">10人以下</span>
                  </div>
                  <div className="text-[20px] font-bold text-emerald-600 ml-[14px]">989</div>
                  <div className="text-[10px] text-slate-400 ml-[14px]">占 90.2%</div>
                </div>
                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg border border-amber-200 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-[11px] font-medium text-amber-700">10-50人</span>
                  </div>
                  <div className="text-[20px] font-bold text-amber-600 ml-[14px]">37</div>
                  <div className="text-[10px] text-slate-400 ml-[14px]">占 3.4%</div>
                </div>
                <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-lg border border-rose-200 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="w-2 h-2 rounded-full bg-rose-500" />
                    <span className="text-[11px] font-medium text-rose-700">50-100人</span>
                  </div>
                  <div className="text-[20px] font-bold text-rose-600 ml-[14px]">7</div>
                  <div className="text-[10px] text-slate-400 ml-[14px]">占 0.6%</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg border border-purple-200 px-3 py-2.5">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="w-2 h-2 rounded-full bg-purple-500" />
                    <span className="text-[11px] font-medium text-purple-700">100人以上</span>
                  </div>
                  <div className="text-[20px] font-bold text-purple-600 ml-[14px]">3</div>
                  <div className="text-[10px] text-slate-400 ml-[14px]">占 0.3%</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {formatTab !== "status" && formatTab !== "duration" && formatTab !== "capital" && formatTab !== "industry" && formatTab !== "insurance" && (
          <div className="flex flex-col items-center justify-center py-10 text-[12px] text-slate-400">
            <i className="ri-pie-chart-2-line text-slate-300 text-3xl block mb-2" />
            <span>暂无数据</span>
          </div>
        )}
      </EnterpriseModal>

      <EnterpriseModal
        visible={activeModal === "directory"}
        title="企业目录"
        width={760}
        onClose={() => setActiveModal(null)}
      >
        <div className="overflow-x-auto">
          {directoryLoading ? (
            <div className="flex flex-col items-center justify-center py-12 text-[12px] text-slate-400">
              <i className="ri-loader-4-line animate-spin text-slate-300 text-2xl mb-2" />
              <span>加载企业数据...</span>
            </div>
          ) : enterpriseList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-[12px] text-slate-400">
              <i className="ri-inbox-line text-slate-300 text-2xl mb-2" />
              <span>暂无企业数据</span>
            </div>
          ) : (
            <>
              <table className="w-full text-[11px] border-collapse table-fixed">
                <thead>
                  <tr className="bg-slate-100 text-slate-600 font-medium">
                    <th className="text-left px-2 py-2" style={{width:'32%'}}>企业名称</th>
                    <th className="text-left px-2 py-2" style={{width:'20%'}}>经济行业</th>
                    <th className="text-left px-2 py-2" style={{width:'10%'}}>法人</th>
                    <th className="text-left px-2 py-2" style={{width:'28%'}}>地址</th>
                    <th className="text-center px-2 py-2" style={{width:'10%'}}>社保</th>
                  </tr>
                </thead>
              </table>
              <div className="max-h-[360px] overflow-y-auto">
                <table className="w-full text-[11px] border-collapse table-fixed">
                  <tbody>
                    {enterpriseList.map((item, i) => (
                      <tr
                        key={i}
                        onClick={() => { setSelectedEnterprise(item._raw); setShowDetail(true); }}
                        className={`border-b border-slate-100 hover:bg-orange-50/40 transition-colors cursor-pointer ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
                      >
                        <td className="px-2 py-2 text-slate-700 font-medium truncate" title={item.name} style={{width:'32%'}}>{item.name}</td>
                        <td className="px-2 py-2 text-slate-500 truncate" title={item.industry || ''} style={{width:'20%'}}>{item.industry || '-'}</td>
                        <td className="px-2 py-2 text-slate-600 truncate" title={item.legalPerson || ''} style={{width:'10%'}}>{item.legalPerson || '-'}</td>
                        <td className="px-2 py-2 text-slate-500 truncate" title={item.address || ''} style={{width:'28%'}}>{item.address || '-'}</td>
                        <td className="px-2 py-2 text-center" style={{width:'10%'}}>
                          {!item.insurance || item.insurance === '未公示' || item.insurance === '-' ? (
                            <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap">{item.insurance || '未公示'}</span>
                          ) : (
                            <span className="text-[10px] font-medium text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded whitespace-nowrap">{item.insurance}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
          {enterpriseList.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between px-2 py-2.5 border-t border-slate-100 bg-slate-50/50">
              <span className="text-[11px] text-slate-400">
                共 <span className="font-medium text-slate-600">{total}</span> 条
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPageNum(1)}
                  disabled={pageNum <= 1}
                  className="w-6 h-6 flex items-center justify-center rounded text-[11px] text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  title="首页"
                >
                  <i className="ri-arrow-left-double-line text-xs" />
                </button>
                <button
                  onClick={() => setPageNum(p => Math.max(1, p - 1))}
                  disabled={pageNum <= 1}
                  className="w-6 h-6 flex items-center justify-center rounded text-[11px] text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  title="上一页"
                >
                  <i className="ri-arrow-left-s-line text-xs" />
                </button>
                <span className="text-[11px] text-slate-600 px-2 min-w-[60px] text-center">
                  {pageNum} / {totalPages}
                </span>
                <button
                  onClick={() => setPageNum(p => Math.min(totalPages, p + 1))}
                  disabled={pageNum >= totalPages}
                  className="w-6 h-6 flex items-center justify-center rounded text-[11px] text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  title="下一页"
                >
                  <i className="ri-arrow-right-s-line text-xs" />
                </button>
                <button
                  onClick={() => setPageNum(totalPages)}
                  disabled={pageNum >= totalPages}
                  className="w-6 h-6 flex items-center justify-center rounded text-[11px] text-slate-500 hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  title="末页"
                >
                  <i className="ri-arrow-right-double-line text-xs" />
                </button>
              </div>
            </div>
          )}
        </div>
      </EnterpriseModal>

      <EnterpriseDetailModal
        visible={showDetail}
        data={selectedEnterprise}
        onClose={() => { setShowDetail(false); setSelectedEnterprise(null); }}
      />
    </>
  );
}
